import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Trans } from 'react-i18next';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { login } from '@/api/auth';
import { MAIN_ROUTE_BY_ROLE, ROUTES } from '@/router/routes';
import { useUserProfile } from '@/states/user-profile/use-user-profile';
import { ALERT_CONFIG, swalert } from '@/libs/sweetalert';
import { toast_error } from '@/libs/toast-wrappers';
import { emailSchema, requiredString } from '@/utils/zod-schemas';

import { TwoFactorAuthVerifyForm } from '../two-factor-auth/verify-form';
import { InputField } from '../form/input-field';
import { Button } from '../ui/button';
import { InputCheckbox } from '../form/input-checkbox';

const schema = z.object({
  email: emailSchema,
  password: requiredString,
});

type LoginFormData = z.infer<typeof schema>;

export function LoginForm() {
  const [show2FA, setShow2FA] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [preAuthToken, setPreAuthToken] = useState('');
  const profile = useUserProfile((state) => state.profile);
  const setAuthenticationData = useUserProfile((state) => state.setAuthenticationData);

  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<LoginFormData>({ resolver: zodResolver(schema) });

  const navigate = useNavigate();

  const onSubmit: SubmitHandler<LoginFormData> = async (data) => {
    setIsLoading(true);
    try {
      const response = await login(data.email, data.password);
      if (!response) {
        setIsLoading(false);
        return;
      }

      if (response && response['2fa_required'] && response.pre_auth_token) {
        setShow2FA(true);
        setPreAuthToken(response.pre_auth_token);
        return;
      }

      setAuthenticationData(response['Access Key']);
      const from = new URLSearchParams(window.location.search).get('from');
      if (from && from !== ROUTES.LOGOUT) {
        navigate(from);
      } else {
        navigate(MAIN_ROUTE_BY_ROLE[profile?.role ?? 'clinicuser']);
      }
      setIsLoading(false);
      reset();

      const answer = await swalert.fire({
        ...ALERT_CONFIG,
        title: t('login-2fa-title'),
        html: `<p style="margin-bottom: 1rem;">${t('login-2fa-description')}</p>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: t('yes'),
        cancelButtonText: t('no'),
      });

      if (answer.isConfirmed) {
        navigate(ROUTES.TWO_FACTOR_AUTH_ENABLE);
      }
    } catch (e) {
      const error = e as Error;
      setIsLoading(false);

      // if we got invalid password we should redirect to change password
      if (error.message.includes('Password is invalid')) {
        toast_error(t('login-error-invalid-password'));
        navigate(ROUTES.CHANGE_PASSWORD);
      }
    }
  };

  return (
    <>
      <Trans i18nKey="login-login-title">
        <div className="title-auth">
          Login to the{' '}
          <a className="footer-link" href="https://www.vitazi.ai/">
            Vitazi.ai
          </a>{' '}
          dashboard
        </div>
      </Trans>
      {show2FA ? (
        <TwoFactorAuthVerifyForm preAuthToken={preAuthToken} />
      ) : (
        <form className="w-full my-12 flex flex-col gap-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="max-w-sm flex flex-col gap-4">
            <InputField
              label={t('login-email')}
              type="email"
              autoComplete="email"
              required
              autoFocus
              tabIndex={1}
              errorMessage={errors.email?.message}
              {...register('email')}
            />
            <InputField
              label={t('login-password')}
              type="password"
              required
              tabIndex={2}
              errorMessage={errors.password?.message}
              {...register('password')}
            />
            <div className="flex justify-end">
              <Button href={ROUTES.RESET_PASSWORD} variant="link" tabIndex={5}>
                {t('login-forgot-pass')}
              </Button>
            </div>
          </div>
          <InputCheckbox
            name="terms"
            id={'terms-accept'}
            required
            label={t('login-terms')}
            tabIndex={3}
          />

          <div className="flex flex-wrap items-center gap-4">
            <Button loading={isLoading} type="submit" className="button" tabIndex={4}>
              {t('login-login')}
            </Button>
            <div className="inline-flex gap-2 items-center">
              <span>{t('login-new-user')}</span>
              <Button href={ROUTES.SIGN_UP} variant="link" tabIndex={6}>
                {t('login-signup-link')}
              </Button>
            </div>
          </div>
        </form>
      )}
    </>
  );
}
